"use client"

import * as React from "react"
import { CalendarIcon, Clock } from "lucide-react"

import { cn } from "../../utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

// ─── Time helpers ────────────────────────────────────────────────────────────

function buildMinutes(step: number): string[] {
  const out: string[] = []
  for (let m = 0; m < 60; m += step) out.push(String(m).padStart(2, "0"))
  return out
}

function parse24h(v: string): { h: number; m: number } | null {
  const parts = v.split(":")
  if (parts.length < 2) return null
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1], 10)
  return isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59
    ? null
    : { h, m }
}

function to24hString(h: number, m: number): string {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

function format12h(h24: number, m: number): string {
  const ampm = h24 < 12 ? "AM" : "PM"
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
}

function snapToStep(m: number, step: number): number {
  const snapped = Math.round(m / step) * step
  return snapped >= 60 ? snapped - 60 : snapped
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0")
)
const AMPM_OPTIONS = ["AM", "PM"]

// ─── TimeCol ─────────────────────────────────────────────────────────────────

interface TimeColProps {
  items: string[]
  selected: string
  onSelect: (v: string) => void
  colRef: React.RefObject<HTMLDivElement>
  hasBorderRight?: boolean
}

function TimeCol({ items, selected, onSelect, colRef, hasBorderRight }: TimeColProps) {
  return (
    <div
      ref={colRef}
      className={cn(
        "flex flex-col overflow-y-auto py-0.5 w-[52px]",
        "[&::-webkit-scrollbar]:w-1",
        "[&::-webkit-scrollbar-thumb]:rounded-full",
        "[&::-webkit-scrollbar-thumb]:bg-border",
        hasBorderRight && "border-r border-border"
      )}
    >
      {items.map((item) => {
        const isSelected = item === selected
        return (
          <button
            key={item}
            type="button"
            data-selected={isSelected ? "true" : undefined}
            onClick={() => onSelect(item)}
            className={cn(
              "h-7 mx-0.5 flex items-center justify-center rounded text-xs transition-colors select-none shrink-0",
              isSelected
                ? "bg-primary text-primary-foreground font-semibold"
                : "text-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            {item}
          </button>
        )
      })}
    </div>
  )
}

// ─── InlineTimePicker (side panel) ───────────────────────────────────────────

interface InlineTimePickerProps {
  label: string
  h24: number
  min: number
  step: number
  onHourChange: (h24: number) => void
  onMinuteChange: (m: number) => void
  disabled?: boolean
  className?: string
}

function InlineTimePicker({
  label,
  h24,
  min,
  step,
  onHourChange,
  onMinuteChange,
  disabled,
  className,
}: InlineTimePickerProps) {
  const minuteItems = React.useMemo(() => buildMinutes(step), [step])
  const h12Display = String(h24 % 12 === 0 ? 12 : h24 % 12).padStart(2, "0")
  const ampmDisplay: "AM" | "PM" = h24 < 12 ? "AM" : "PM"
  const minDisplay = String(min).padStart(2, "0")

  const hourRef = React.useRef<HTMLDivElement>(null)
  const minuteRef = React.useRef<HTMLDivElement>(null)
  const ampmRef = React.useRef<HTMLDivElement>(null)

  // Scroll selected into view on mount
  React.useEffect(() => {
    requestAnimationFrame(() => {
      ;[hourRef, minuteRef, ampmRef].forEach((ref) => {
        if (!ref.current) return
        const el = ref.current.querySelector('[data-selected="true"]') as HTMLElement | null
        if (el) {
          ref.current.scrollTop =
            el.offsetTop - ref.current.clientHeight / 2 + el.clientHeight / 2
        }
      })
    })
  }, [])

  const handleHour12 = (hStr: string) => {
    if (disabled) return
    const h = parseInt(hStr, 10)
    onHourChange(
      ampmDisplay === "AM"
        ? h === 12 ? 0 : h
        : h === 12 ? 12 : h + 12
    )
  }

  const handleMinute = (mStr: string) => {
    if (disabled) return
    onMinuteChange(parseInt(mStr, 10))
  }

  const handleAmpm = (ap: string) => {
    if (disabled) return
    if (ap === "AM" && ampmDisplay === "PM") onHourChange(h24 - 12)
    else if (ap === "PM" && ampmDisplay === "AM") onHourChange(h24 + 12)
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Label */}
      <div className="flex items-center gap-1.5 px-2 py-2">
        <Clock className="h-3 w-3 text-muted-foreground" />
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      {/* Column headers */}
      <div className="flex border-y border-border bg-muted/30">
        <div className="w-[52px] py-1 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border">
          HR
        </div>
        <div className="w-[52px] py-1 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border">
          MIN
        </div>
        <div className="w-[52px] py-1 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          AM/PM
        </div>
      </div>
      {/* Scrollable columns */}
      <div className="flex flex-1 min-h-0">
        <TimeCol
          items={HOURS_12}
          selected={h12Display}
          onSelect={handleHour12}
          colRef={hourRef}
          hasBorderRight
        />
        <TimeCol
          items={minuteItems}
          selected={minDisplay}
          onSelect={handleMinute}
          colRef={minuteRef}
          hasBorderRight
        />
        <TimeCol
          items={AMPM_OPTIONS}
          selected={ampmDisplay}
          onSelect={handleAmpm}
          colRef={ampmRef}
        />
      </div>
    </div>
  )
}

// ─── TimeDropdownRow (compact row with HR / MIN / AM-PM dropdowns) ──────────

export interface TimeDropdownRowProps {
  label: string
  h24: number
  min: number
  onHourChange: (h24: number) => void
  onMinuteChange: (m: number) => void
  className?: string
}

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const val = i + 1
  return { value: String(val), label: String(val).padStart(2, "0") }
})

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: String(i).padStart(2, "0"),
}))

export function TimeDropdownRow({
  label,
  h24,
  min,
  onHourChange,
  onMinuteChange,
  className,
}: TimeDropdownRowProps) {
  const ampm: "AM" | "PM" = h24 < 12 ? "AM" : "PM"
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12

  const handleHour12Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newH12 = parseInt(e.target.value, 10)
    onHourChange(ampm === "AM" ? (newH12 === 12 ? 0 : newH12) : (newH12 === 12 ? 12 : newH12 + 12))
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onMinuteChange(parseInt(e.target.value, 10))
  }

  const handleAmpmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAmpm = e.target.value as "AM" | "PM"
    if (newAmpm === "AM" && ampm === "PM") onHourChange(h24 - 12)
    else if (newAmpm === "PM" && ampm === "AM") onHourChange(h24 + 12)
  }

  const selectClass =
    "h-7 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="text-xs font-medium text-muted-foreground w-10 shrink-0">{label}</span>
      <select value={String(h12)} onChange={handleHour12Change} className={cn(selectClass, "w-14")}>
        {HOUR_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="text-xs text-muted-foreground">:</span>
      <select value={String(min)} onChange={handleMinuteChange} className={cn(selectClass, "w-14")}>
        {MINUTE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <select value={ampm} onChange={handleAmpmChange} className={cn(selectClass, "w-14")}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  )
}

// ─── Exported sub-components ─────────────────────────────────────────────────

export { InlineTimePicker }
export type { InlineTimePickerProps }

// ─── Public types ────────────────────────────────────────────────────────────

export interface CalendarWithTimeProps {
  /** The selected date value */
  value?: Date
  /** Callback when the date changes */
  onDateChange?: (date: Date | undefined) => void
  /** Time display mode: "range" shows start+end, "single" shows one time, "none" hides time */
  timeMode?: "range" | "single" | "none"
  /** Start time in 24h "HH:MM" format (used in "range" mode) */
  startTime?: string
  /** End time in 24h "HH:MM" format (used in "range" mode) */
  endTime?: string
  /** Single time value in 24h "HH:MM" format (used in "single" mode) */
  time?: string
  /** Callback when start time changes — returns 24h "HH:MM" (range mode) */
  onStartTimeChange?: (time: string) => void
  /** Callback when end time changes — returns 24h "HH:MM" (range mode) */
  onEndTimeChange?: (time: string) => void
  /** Callback when time changes — returns 24h "HH:MM" (single mode) */
  onTimeChange?: (time: string) => void
  /** Label for the single time field (single mode). Defaults to "Time" */
  timeLabel?: string
  /** Minute step for the time picker columns. Default: 1. */
  minuteStep?: number
  /** Placeholder text for the trigger button */
  placeholder?: string
  /** Custom class name for the root element */
  className?: string
  /** Disabled state */
  disabled?: boolean
  /** Minimum selectable date */
  minDate?: Date
  /** Maximum selectable date */
  maxDate?: Date
  /** @deprecated Use minuteStep instead */
  showSeconds?: boolean
}

// ─── Main component ──────────────────────────────────────────────────────────

export function CalendarWithTime({
  value,
  onDateChange,
  timeMode = "range",
  startTime = "10:30",
  endTime = "12:30",
  time = "10:30",
  onStartTimeChange,
  onEndTimeChange,
  onTimeChange,
  timeLabel = "Time",
  minuteStep = 1,
  placeholder = "Select date & time",
  className,
  disabled = false,
  minDate,
  maxDate,
}: CalendarWithTimeProps) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(value)

  // ── Single time state ──────────────────────────────────────────────────
  const parsedTime = parse24h(time)
  const [sH24, setSH24] = React.useState(parsedTime?.h ?? 10)
  const [sMin, setSMin] = React.useState(snapToStep(parsedTime?.m ?? 30, minuteStep))

  // ── Range time state ───────────────────────────────────────────────────
  const parsedStart = parse24h(startTime)
  const parsedEnd = parse24h(endTime)
  const [startH24, setStartH24] = React.useState(parsedStart?.h ?? 10)
  const [startMin, setStartMin] = React.useState(snapToStep(parsedStart?.m ?? 30, minuteStep))
  const [endH24, setEndH24] = React.useState(parsedEnd?.h ?? 12)
  const [endMin, setEndMin] = React.useState(snapToStep(parsedEnd?.m ?? 30, minuteStep))

  // Sync controlled props
  React.useEffect(() => { setDate(value) }, [value])

  React.useEffect(() => {
    const p = parse24h(time)
    if (p) { setSH24(p.h); setSMin(snapToStep(p.m, minuteStep)) }
  }, [time, minuteStep])

  React.useEffect(() => {
    const p = parse24h(startTime)
    if (p) { setStartH24(p.h); setStartMin(snapToStep(p.m, minuteStep)) }
  }, [startTime, minuteStep])

  React.useEffect(() => {
    const p = parse24h(endTime)
    if (p) { setEndH24(p.h); setEndMin(snapToStep(p.m, minuteStep)) }
  }, [endTime, minuteStep])

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleDateChange = (newDate: Date | undefined) => {
    setDate(newDate)
    onDateChange?.(newDate)
  }

  const handleSingleHour = (h: number) => { setSH24(h); onTimeChange?.(to24hString(h, sMin)) }
  const handleSingleMin = (m: number) => { setSMin(m); onTimeChange?.(to24hString(sH24, m)) }

  const handleStartHour = (h: number) => { setStartH24(h); onStartTimeChange?.(to24hString(h, startMin)) }
  const handleStartMin = (m: number) => { setStartMin(m); onStartTimeChange?.(to24hString(startH24, m)) }
  const handleEndHour = (h: number) => { setEndH24(h); onEndTimeChange?.(to24hString(h, endMin)) }
  const handleEndMin = (m: number) => { setEndMin(m); onEndTimeChange?.(to24hString(endH24, m)) }

  // ── Format display text ────────────────────────────────────────────────
  const displayText = React.useMemo(() => {
    if (!date) return placeholder

    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })

    if (timeMode === "none") return dateStr
    if (timeMode === "single") return `${dateStr}, ${format12h(sH24, sMin)}`
    return `${dateStr}, ${format12h(startH24, startMin)} – ${format12h(endH24, endMin)}`
  }, [date, timeMode, sH24, sMin, startH24, startMin, endH24, endMin, placeholder])

  // ── Summary line for the footer ────────────────────────────────────────
  const summaryText = React.useMemo(() => {
    if (!date) return null
    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    if (timeMode === "none") return dateStr
    if (timeMode === "single") return `${dateStr} at ${format12h(sH24, sMin)}`
    return `${dateStr}, ${format12h(startH24, startMin)} – ${format12h(endH24, endMin)}`
  }, [date, timeMode, sH24, sMin, startH24, startMin, endH24, endMin])

  const hasTime = timeMode !== "none"

  // Determine the height for the time panel to match calendar height
  // In range mode we stack two pickers, in single mode one picker stretches
  const TIME_PANEL_HEIGHT = 300

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-9",
            !date && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{displayText}</span>
          <CalendarIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-auto p-0 overflow-hidden"
        sideOffset={4}
      >
        <div className="flex">
          {/* ── Left: Calendar ────────────────────────────────────────── */}
          <div className={cn("p-3", hasTime && "border-r border-border")}>
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateChange}
              disabled={(d) => {
                if (minDate && d < minDate) return true
                if (maxDate && d > maxDate) return true
                return false
              }}
              className="border-0 shadow-none p-0"
            />
          </div>

          {/* ── Right: Time picker(s) ────────────────────────────────── */}
          {timeMode === "single" && (
            <div
              className="flex flex-col"
              style={{ height: TIME_PANEL_HEIGHT }}
            >
              <InlineTimePicker
                label={timeLabel}
                h24={sH24}
                min={sMin}
                step={minuteStep}
                onHourChange={handleSingleHour}
                onMinuteChange={handleSingleMin}
                disabled={disabled}
                className="flex-1 min-h-0"
              />
            </div>
          )}

          {timeMode === "range" && (
            <div
              className="flex flex-col"
              style={{ height: TIME_PANEL_HEIGHT }}
            >
              <InlineTimePicker
                label="Start Time"
                h24={startH24}
                min={startMin}
                step={minuteStep}
                onHourChange={handleStartHour}
                onMinuteChange={handleStartMin}
                disabled={disabled}
                className="flex-1 min-h-0 border-b border-border"
              />
              <InlineTimePicker
                label="End Time"
                h24={endH24}
                min={endMin}
                step={minuteStep}
                onHourChange={handleEndHour}
                onMinuteChange={handleEndMin}
                disabled={disabled}
                className="flex-1 min-h-0"
              />
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-2">
          <span className="text-xs text-muted-foreground truncate mr-3">
            {summaryText ?? "No date selected"}
          </span>
          <Button
            size="sm"
            type="button"
            className="h-7 px-4 text-xs shrink-0"
            onClick={() => setOpen(false)}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
